import React, { RefObject, useCallback, useContext, useEffect, useMemo } from 'react'
import { normaliseOccupancies } from 'businessTraveller/utils/normaliseOccupancies'
import setSelectedBusinessTravellersAndGuests from 'actions/businessTraveller/setSelectedBusinessTravellerEmployeesAndGuests'
import { useAppDispatch } from 'hooks/reduxHooks'
import { connect } from 'react-redux'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { SearchMenuStates } from 'components/Search/type'
import useBusinessTravellerEmployeeQuery from 'businessTraveller/hooks/useEmployeeQuery'
import { selectBusinessTravellerCurrentApprovalRequest } from 'businessTraveller/selectors/selectBusinessTravellerCurrentApprovalRequest'
import { sum } from 'lib/array/arrayUtils'
import BusinessTravellerSelectHotelsTravellersDesktop from './BusinessTravellerSelectHotelsTravellersDesktop'
import { saveBusinessTravellerSelectedTravellerState } from 'businessTraveller/storage/businessTravellerStorage'

interface Props {
  activeMenu: SearchMenuStates
  onClose: () => void
  onApply: () => void
  inputRef: RefObject<HTMLButtonElement | null>
  totalTravellers: number
}

interface MappedStateProps {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  guestCount: number
  approvalRequest?: App.BusinessTraveller.ApprovalRequest
}

function BusinessTravellerSelectHotelTravellers(props: Props & MappedStateProps) {
  const { travellerEmployees, guestCount, activeMenu, onClose, inputRef, totalTravellers, approvalRequest, onApply } = props

  const { employees } = useBusinessTravellerEmployeeQuery()

  const globalSearchState = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const occupancies = useMemo(() => globalSearchState.occupancies ?? [], [globalSearchState.occupancies])

  const appDispatch = useAppDispatch()

  const setOccupants = useCallback((employeeIds: Array<string>, guestCount: number, roomCount?: number) => {
    const travellerCount = employeeIds.length + guestCount
    appDispatch(setSelectedBusinessTravellersAndGuests(employeeIds, guestCount))
    const currentOccupancies = globalSearchState.occupancies ?? []
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies: normaliseOccupancies(travellerCount, roomCount ?? Math.min(currentOccupancies.length, travellerCount)) })
    // Save to localstorage so persist on page refresh
    saveBusinessTravellerSelectedTravellerState(employeeIds, guestCount)
  }, [appDispatch, globalSearchDispatch, globalSearchState.occupancies])

  // This useEffect ensures that the selected travellers are always valid.
  // Since the selected employee state (travellerEmployees, guestCount) is shared with Flights and Car Hire,
  // it is possible for them to fall out of sync with the occupancies state. We reconcile that here.
  useEffect(() => {
    if (!employees) return

    const validEmployeeIds = new Set(Object.keys(employees))
    const currentOccupancyCount = sum(occupancies, room => room.adults)
    const selectedTravellerCount = guestCount + travellerEmployees.length
    const hasInvalidSelection =
      (selectedTravellerCount !== currentOccupancyCount && currentOccupancyCount > 1) ||
      travellerEmployees.some(
        employee => !validEmployeeIds.has(employee.id),
      )

    if (hasInvalidSelection) {
      const validSelectedTravellers = travellerEmployees.filter(
        employee => validEmployeeIds.has(employee.id),
      ).map(employee => employee.id)
      setOccupants(validSelectedTravellers, guestCount)
    }
  }, [employees, setOccupants, occupancies, travellerEmployees, guestCount])

  const onAddEmployee = useCallback((employeeId: string) => {
    const selectedEmployeeIds = [...travellerEmployees.map(employee => employee.id), employeeId]
    const newOccupantCount = selectedEmployeeIds.length + guestCount

    if (approvalRequest) {
      showSnackbar(
        'If you need to change the travellers, please create a new approval request.',
        'warning',
      )
      return
    }
    // Add new room when adding employee
    if (newOccupantCount > 1) {
      setOccupants(selectedEmployeeIds, guestCount, occupancies.length + 1)
    } else {
      setOccupants(selectedEmployeeIds, guestCount)
    }
  }, [guestCount, travellerEmployees, setOccupants, approvalRequest, occupancies])

  const onRemoveEmployee = useCallback((employeeId: string) => {
    const selectedEmployeeIds = travellerEmployees.filter(employee => employee.id !== employeeId).map(employee => employee.id)
    const newOccupantCount = selectedEmployeeIds.length + guestCount
    // Remove a room if there will be any empty rooms
    if (newOccupantCount < occupancies.length) {
      setOccupants(selectedEmployeeIds, guestCount, (occupancies.length - 1) || 1)
    } else {
      setOccupants(selectedEmployeeIds, guestCount)
    }
  }, [travellerEmployees, guestCount, occupancies.length, setOccupants])

  const onRemoveGuest = useCallback(() => {
    const selectedEmployeeIds = travellerEmployees.map(employee => employee.id)
    const newOccupantCount = selectedEmployeeIds.length + guestCount - 1
    // Remove a room if there will be any empty rooms
    if (newOccupantCount < occupancies.length) {
      setOccupants(selectedEmployeeIds, guestCount - 1, (occupancies.length - 1) || 1)
    } else {
      setOccupants(selectedEmployeeIds, guestCount - 1)
    }
  }, [travellerEmployees, guestCount, occupancies.length, setOccupants])

  const onAddGuest = useCallback(() => {
    const newOccupantCount = travellerEmployees.length + guestCount + 1

    if (approvalRequest) {
      showSnackbar(
        'If you need to change the travellers, please create a new approval request.',
        'warning',
      )
      return
    }
    // Add new room when adding guest
    if (newOccupantCount > 1) {
      setOccupants(travellerEmployees.map(employee => employee.id), guestCount + 1, occupancies.length + 1)
    } else {
      setOccupants(travellerEmployees.map(employee => employee.id), guestCount + 1)
    }
  }, [travellerEmployees, guestCount, setOccupants, approvalRequest, occupancies])

  return (
    <BusinessTravellerSelectHotelsTravellersDesktop
      activeMenu={activeMenu}
      onClose={onClose}
      inputRef={inputRef}
      totalTravellers={totalTravellers}
      onAddEmployee={onAddEmployee}
      onRemoveEmployee={onRemoveEmployee}
      onAddGuest={onAddGuest}
      onRemoveGuest={onRemoveGuest}
      onApply={onApply}
    />
  )
}

export default connect<MappedStateProps, {}, Props, App.State>((state) => {
  return {
    travellerEmployees: selectSelectedTravellerEmployees(state),
    guestCount: state.businessTraveller.selectedTravellerGuestCount,
    approvalRequest: selectBusinessTravellerCurrentApprovalRequest(state),
  }
})(BusinessTravellerSelectHotelTravellers)
